"use client";


import Link from "@/infrastructure/router/Link";
import { useRouter } from "@/infrastructure/router/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useArtistSession } from "@/components/auth/ArtistSessionProvider";
import {
	ArtistIcon,
	Avatar,
	EmptyState,
	GlassCard,
	GlassIconButton,
	GlassTextField,
	NavigationBar,
	Skeleton,
	Toast,
} from "@/components/design-system/ArtistDesignSystem";
import {
	type Conversation,
	offlineSocialRepository,
	type SocialRepository,
	sameScope,
	scopeKey,
} from "@/domain/social/SocialRepository";


const SELF_PARTICIPANT_MARKER = "你";
const UNKNOWN_USER_LABEL = "未知用户";
const SKELETON_IDS = [
	"messages-skeleton-1",
	"messages-skeleton-2",
	"messages-skeleton-3",
] as const;


type MessagesInboxDialog =
	| { readonly kind: "compose" }
	| { readonly kind: "markAllRead" }
	| {
			readonly kind: "rowAction";
			readonly conversation: Conversation;
	  };


function useFence() {
	const scope = useArtistSession().scope;
	const current = useRef(scope);
	const generations = useRef(new Map<string, number>());
	useEffect(() => {
		current.current = scope;
		generations.current.clear();
		return () => {
			current.current = null;
			generations.current.clear();
		};
	}, [scope]);
	return useCallback((requestKey: string) => {
		const expectedScope = current.current;
		const expectedGeneration = (generations.current.get(requestKey) ?? 0) + 1;
		generations.current.set(requestKey, expectedGeneration);
		return () =>
			sameScope(current.current, expectedScope) &&
			generations.current.get(requestKey) === expectedGeneration;
	}, []);
}


export function MessagesInbox({
	repository = offlineSocialRepository,
}: Readonly<{ repository?: SocialRepository }>) {
	const auth = useArtistSession();
	const router = useRouter();
	const fence = useFence();
	const [rows, setRows] = useState<readonly Conversation[] | null>(null);
	const [query, setQuery] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [notice, setNotice] = useState<string | null>(null);
	const [composeRecipient, setComposeRecipient] = useState("");
	const [dialog, setDialog] = useState<MessagesInboxDialog | null>(null);
	const [hiddenIds, setHiddenIds] = useState<ReadonlySet<string>>(
		() => new Set(),
	);
	const [blockedIds, setBlockedIds] = useState<ReadonlySet<string>>(
		() => new Set(),
	);

	useEffect(() => {
		if (auth.scope === null) return;
		const applies = fence("inbox.load");
		setRows(null);
		setError(null);
		repository
			.loadInbox(auth.scope)
			.then((value) => {
				if (applies()) setRows(value);
			})
			.catch(() => {
				if (applies()) setError("消息加载失败，请稍后重试。");
			});
	}, [auth.scope, fence, repository]);

	const visibleRows = useMemo(() => {
		if (rows === null) return null;
		const trimmed = query.trim().toLowerCase();
		return rows.filter((row) => {
			if (hiddenIds.has(row.id) || blockedIds.has(row.id) || row.blocked === true) return false;
			if (trimmed.length === 0) return true;
			return row.participants.join(" ").toLowerCase().includes(trimmed);
		});
	}, [blockedIds, hiddenIds, query, rows]);

	const unreadCount = useMemo(() => {
		if (rows === null) return 0;
		return rows.reduce((total, row) => {
			if (hiddenIds.has(row.id) || blockedIds.has(row.id) || row.blocked === true) return total;
			return total + Math.max(0, row.unread);
		}, 0);
	}, [hiddenIds, blockedIds, rows]);

	const handleMarkRowAsRead = useCallback(
		async (conversation: Conversation) => {
			setDialog(null);
			const activeScope = auth.scope;
			if (activeScope === null) return;
			if (conversation.unread <= 0) return;
			setRows((current) =>
				(current ?? []).map((row) =>
					row.id === conversation.id ? { ...row, unread: 0 } : row,
				),
			);
			try {
				await repository.markRead(activeScope, conversation.id);
			} catch {
				setNotice("标记已读未完成，请稍后重试。");
			}
		},
		[auth.scope, repository],
	);

	const handleDeleteRow = useCallback(async (conversation: Conversation) => {
		setDialog(null);
		const activeScope = auth.scope;
		if (activeScope === null) return;
		setHiddenIds((current) => {
			const next = new Set(current);
			next.add(conversation.id);
			return next;
		});
		try {
			await repository.deleteConversation(activeScope, conversation.id);
			setNotice("会话已从收件箱移除。");
		} catch {
			setNotice("删除会话失败，请稍后重试。");
		}
	}, [auth.scope, repository]);

	const handleBlockRow = useCallback(async (conversation: Conversation) => {
		setDialog(null);
		const activeScope = auth.scope;
		if (activeScope === null) return;
		setBlockedIds((current) => {
			const next = new Set(current);
			next.add(conversation.id);
			return next;
		});
		try {
			await repository.setConversationBlocked(activeScope, conversation.id, true);
			setNotice("已屏蔽该会话，对方将无法继续发送消息。");
		} catch {
			setNotice("屏蔽会话失败，请稍后重试。");
		}
	}, [auth.scope, repository]);

	const handleMarkAllRead = useCallback(() => {
		setDialog(null);
		const activeScope = auth.scope;
		if (activeScope === null || rows === null) return;
		const unreadRows = rows.filter(
			(row) =>
				row.unread > 0 && !hiddenIds.has(row.id) && !blockedIds.has(row.id),
		);
		if (unreadRows.length === 0) {
			setNotice("没有未读消息。");
			return;
		}
		setRows((current) =>
			(current ?? []).map((row) =>
				row.unread > 0 && !hiddenIds.has(row.id) && !blockedIds.has(row.id)
					? { ...row, unread: 0 }
					: row,
			),
		);
		Promise.all(
			unreadRows.map((row) => repository.markRead(activeScope, row.id)),
		)
			.then(() => {
				setNotice(`已将 ${unreadRows.length} 个会话标记为已读。`);
			})
			.catch(() => {
				setNotice("部分会话标记未完成，请稍后重试。");
			});
	}, [auth.scope, blockedIds, hiddenIds, repository, rows]);

	const handleCompose = useCallback(async () => {
		const activeScope = auth.scope;
		const recipient = composeRecipient.trim();
		if (activeScope === null || recipient.length === 0) {
			setNotice("请输入联系人名称。");
			return;
		}
		try {
			const conversation = await repository.createConversation(
				activeScope,
				recipient,
			);
			setRows(await repository.loadInbox(activeScope));
			setComposeRecipient("");
			setDialog(null);
			router.push(`/messages/thread/${conversation.id}`);
		} catch {
			setNotice("无法创建会话，请稍后重试。");
		}
	}, [auth.scope, composeRecipient, repository, router]);

	if (auth.phase === "loading") {
		return (
			<section
				className="native-page messages-inbox-page"
				aria-busy="true"
				aria-label="正在载入消息"
			>
				<NavigationBar title="消息" backHref="/" />
				<MessagesInboxSkeleton />
			</section>
		);
	}

	if (auth.phase === "error") {
		return (
			<section className="native-page messages-inbox-page" aria-label="消息">
				<NavigationBar title="消息" backHref="/" />
				<Toast style="error">消息暂时无法载入。</Toast>
			</section>
		);
	}

	if (auth.phase === "guest" || auth.session === null) {
		return (
			<section className="native-page messages-inbox-page" aria-label="消息">
				<NavigationBar title="消息" backHref="/" />
				<EmptyState
					title="登录后查看消息"
					message="登录后即可接收系统通知、互动提醒与好友私信。"
					symbol={<ArtistIcon name="message" size={42} />}
				/>
				<Link className="primary-action" href="/auth">
					登录
				</Link>
			</section>
		);
	}

	if (rows === null) {
		return (
			<section
				className="native-page messages-inbox-page"
				aria-busy="true"
				aria-label="正在载入消息"
			>
				<NavigationBar title="消息" backHref="/" />
				<MessagesInboxSkeleton />
			</section>
		);
	}

	const hasUnread = unreadCount > 0;
	const trailingActions = (
		<div className="messages-inbox-toolbar">
			<button
				type="button"
				className="messages-inbox-toolbar__read-all"
				aria-label="全部已读"
				disabled={!hasUnread}
				onClick={() => setDialog({ kind: "markAllRead" })}
			>
				<ArtistIcon name="check" size={18} strokeWidth={2} />
				<span>全部已读</span>
			</button>
			<GlassIconButton
				label="新建会话"
				onClick={() => setDialog({ kind: "compose" })}
			>
				<ArtistIcon name="plus" size={22} strokeWidth={1.9} />
			</GlassIconButton>
		</div>
	);

	return (
		<section className="native-page messages-inbox-page" aria-label="消息">
			<NavigationBar title="消息" backHref="/" trailing={trailingActions} />
			{notice !== null ? (
				<Toast
					style={
						notice.includes("失败") || notice.includes("未完成")
							? "error"
							: "info"
					}
					onClose={() => setNotice(null)}
				>
					{notice}
				</Toast>
			) : null}
			{error !== null ? <Toast style="error">{error}</Toast> : null}

			<div className="messages-inbox-scroll">
				<GlassTextField
					className="messages-inbox-search"
					label="搜索会话"
					placeholder="搜索参与者"
					leading={<ArtistIcon name="search" size={18} />}
					value={query}
					onChange={(event) => setQuery(event.currentTarget.value)}
				/>

				<GlassCard
					className="messages-inbox-list"
					cornerRadius={16}
					style={{ padding: 0 }}
				>
					{visibleRows === null || visibleRows.length === 0 ? (
						<EmptyState
							className="messages-inbox-empty"
							title={query.trim().length === 0 ? "暂无消息" : "没有匹配的会话"}
							message={
								query.trim().length === 0
									? "新的互动与私信会出现在这里。"
									: "尝试更换关键词，或清空搜索查看全部会话。"
							}
							symbol={<ArtistIcon name="message" size={36} />}
						/>
					) : (
						<ul className="messages-inbox-list__items" aria-label="会话列表">
							{visibleRows.map((conversation, index) => (
								<li key={conversation.id} className="messages-inbox-list__item">
									{index > 0 ? (
										<span
											className="messages-inbox-divider"
											aria-hidden="true"
										/>
									) : null}
									<ConversationRow
										conversation={conversation}
										onOpenActions={() =>
											setDialog({ kind: "rowAction", conversation })
										}
									/>
								</li>
							))}
						</ul>
					)}
				</GlassCard>
			</div>

			<MessagesInboxDialog
				dialog={dialog}
				composeRecipient={composeRecipient}
				onComposeRecipientChange={setComposeRecipient}
				onClose={() => setDialog(null)}
				onComposeConfirm={() => void handleCompose()}
				onMarkAllReadConfirm={handleMarkAllRead}
				onMarkRowRead={handleMarkRowAsRead}
				onDeleteRow={handleDeleteRow}
				onBlockRow={handleBlockRow}
			/>
		</section>
	);
}


interface ConversationRowProps {
	readonly conversation: Conversation;
	readonly onOpenActions: (conversation: Conversation) => void;
}

function ConversationRow({
	conversation,
	onOpenActions,
}: Readonly<ConversationRowProps>) {
	const title = formatParticipants(conversation.participants);
	const hasUnread = conversation.unread > 0;
	return (
		<div className="messages-inbox-row">
			<Link
				className="messages-inbox-row__main"
				href={`/messages/thread/${conversation.id}`}
				aria-label={`打开与 ${title} 的会话`}
			>
				<span className="messages-inbox-row__avatar">
					<Avatar name={title} size={44} />
					{hasUnread ? (
						<i className="messages-inbox-row__dot" aria-hidden="true" />
					) : null}
				</span>
				<span className="messages-inbox-row__copy">
					<span className="messages-inbox-row__heading">
						<strong>{title}</strong>
						<time>{conversation.at}</time>
					</span>
					<small>{conversation.snippet}</small>
				</span>
			</Link>
			<button
				type="button"
				className="messages-inbox-row__more"
				aria-label={`更多操作 · ${title}`}
				onClick={() => onOpenActions(conversation)}
			>
				<ArtistIcon name="more" size={20} />
			</button>
		</div>
	);
}


function MessagesInboxSkeleton() {
	return (
		<div className="messages-inbox-skeleton" aria-hidden="true">
			<Skeleton shape="rectangle" width="100%" height={48} />
			<GlassCard
				className="messages-inbox-list"
				cornerRadius={16}
				style={{ padding: 0 }}
			>
				<ul className="messages-inbox-list__items">
					{SKELETON_IDS.map((skeletonId, skeletonIndex) => (
						<li key={skeletonId} className="messages-inbox-list__item">
							{skeletonIndex > 0 ? (
								<span className="messages-inbox-divider" aria-hidden="true" />
							) : null}
							<div className="messages-inbox-row messages-inbox-row--placeholder">
								<span className="messages-inbox-row__avatar">
									<Skeleton shape="circle" width={44} height={44} />
								</span>
								<span className="messages-inbox-row__copy">
									<span className="messages-inbox-row__heading">
										<Skeleton shape="rectangle" width={120} height={16} />
										<Skeleton shape="rectangle" width={54} height={12} />
									</span>
									<Skeleton shape="rectangle" width="100%" height={14} />
								</span>
							</div>
						</li>
					))}
				</ul>
			</GlassCard>
		</div>
	);
}


interface MessagesInboxDialogProps {
	readonly dialog: MessagesInboxDialog | null;
	readonly composeRecipient: string;
	readonly onComposeRecipientChange: (value: string) => void;
	readonly onClose: () => void;
	readonly onComposeConfirm: () => void;
	readonly onMarkAllReadConfirm: () => void;
	readonly onMarkRowRead: (conversation: Conversation) => void;
	readonly onDeleteRow: (conversation: Conversation) => void;
	readonly onBlockRow: (conversation: Conversation) => void;
}

function MessagesInboxDialog({
	dialog,
	composeRecipient,
	onComposeRecipientChange,
	onClose,
	onComposeConfirm,
	onMarkAllReadConfirm,
	onMarkRowRead,
	onDeleteRow,
	onBlockRow,
}: Readonly<MessagesInboxDialogProps>) {
	const dialogRef = useRef<HTMLDialogElement>(null);

	useEffect(() => {
		const element = dialogRef.current;
		if (element === null) return;
		const open = dialog !== null;
		if (open) {
			if (!element.open && typeof element.showModal === "function") {
				element.showModal();
			}
		} else if (element.open) {
			element.close();
		}
	}, [dialog]);

	const title = useMemo(() => {
		if (dialog === null) return "";
		if (dialog.kind === "rowAction") {
			return formatParticipants(dialog.conversation.participants);
		}
		if (dialog.kind === "compose") return "新建私信";
		return "全部标记为已读？";
	}, [dialog]);

	return (
		<dialog
			ref={dialogRef}
			className="messages-inbox-dialog"
			aria-labelledby="messages-inbox-dialog-title"
			onCancel={(event) => {
				event.preventDefault();
				onClose();
			}}
			onClick={(event) => {
				if (event.target === event.currentTarget) onClose();
			}}
			onKeyDown={(event) => {
				if (event.key === "Escape") {
					event.preventDefault();
					onClose();
				}
			}}
		>
			{dialog === null ? null : (
				<div className="messages-inbox-dialog__body">
					<h2 id="messages-inbox-dialog-title">{title}</h2>
					{dialog.kind === "compose" ? (
						<>
							<GlassTextField
								label="联系人名称"
								placeholder="输入联系人名称"
								leading={<ArtistIcon name="person" size={18} />}
								value={composeRecipient}
								onChange={(event) =>
									onComposeRecipientChange(event.currentTarget.value)
								}
							/>
							<div className="messages-inbox-dialog__actions">
								<button
									type="button"
									className="messages-inbox-dialog__primary"
									onClick={onComposeConfirm}
									disabled={composeRecipient.trim().length === 0}
								>
									开始对话
								</button>
								<button
									type="button"
									className="messages-inbox-dialog__secondary"
									onClick={onClose}
								>
									取消
								</button>
							</div>
						</>
					) : dialog.kind === "markAllRead" ? (
						<>
							<p>所有未读会话将标记为已读，未读红点会同步消失。</p>
							<div className="messages-inbox-dialog__actions">
								<button
									type="button"
									className="messages-inbox-dialog__primary"
									onClick={onMarkAllReadConfirm}
								>
									全部已读
								</button>
								<button
									type="button"
									className="messages-inbox-dialog__secondary"
									onClick={onClose}
								>
									取消
								</button>
							</div>
						</>
					) : (
						<>
							<p>
								选择对「{title}
								」的操作。删除仅移出当前列表，屏蔽后对方将无法继续发送消息。
							</p>
							<div className="messages-inbox-dialog__actions messages-inbox-dialog__actions--stack">
								<button
									type="button"
									className="messages-inbox-dialog__option"
									onClick={() => onMarkRowRead(dialog.conversation)}
									disabled={dialog.conversation.unread <= 0}
								>
									<ArtistIcon name="check" size={20} />
									<span>标记已读</span>
								</button>
								<button
									type="button"
									className="messages-inbox-dialog__option"
									onClick={() => onDeleteRow(dialog.conversation)}
								>
									<ArtistIcon name="trash" size={20} />
									<span>删除会话</span>
								</button>
								<button
									type="button"
									className="messages-inbox-dialog__option messages-inbox-dialog__option--destructive"
									onClick={() => onBlockRow(dialog.conversation)}
								>
									<ArtistIcon name="shield" size={20} />
									<span>屏蔽会话</span>
								</button>
								<button
									type="button"
									className="messages-inbox-dialog__secondary"
									onClick={onClose}
								>
									取消
								</button>
							</div>
						</>
					)}
				</div>
			)}
		</dialog>
	);
}


function formatParticipants(participants: readonly string[]): string {
	const others = participants.filter(
		(name) => name !== SELF_PARTICIPANT_MARKER,
	);
	if (others.length === 0) return UNKNOWN_USER_LABEL;
	return others.join("、");
}

export { scopeKey };
